// The product's map look, from the design mockups (bridges/design/16.png,
// 17.png): a quiet cream sheet with grey streets, blue-grey highways, pale
// green parks, few labels and no shop/café icons — the artifacts and places
// are the only things on the map that call for attention. MapTiler
// streets-v2 is repainted to it layer by layer (the layer ids are the
// style's own). The same theme as the mobile app (bridges-mobile
// src/lib/map/theme.ts) and the front-end demo — keep the three in step.

const LAND = '#FAF9F1';
const PARK = '#DDF2C4';
const WATER = '#D6DEE9';
const WATER_LABEL = '#8A9BB2';
const BUILDING = '#EFEEE5';
const ROAD = '#D2D4DB';
const ROAD_MINOR = '#DCDEE3';
const PATH = '#E6E7EA';
const HIGHWAY = '#D5DAE5';
const HIGHWAY_EDGE = '#A7AFC0';
const RAIL = '#DADBE0';
const BORDER = '#C9C9C9';
const LABEL = '#9A9A9A';
const LABEL_STRONG = '#111111';
const HALO = '#FFFFFF';

// Hidden: map furniture that competes with the artifacts.
const HIDDEN = new Set([
  'Residential',
  'Industrial',
  'Hospital',
  'School',
  'Stadium',
  'Cemetery',
  'Airport zone',
  'Glacier',
  'Sand',
  'Crop',
  'Heliport',
  'Ferry line',
  'Railway tunnel hatching',
  'Major rail hatching',
  'Minor rail hatching',
  'Footway tunnel outline',
  'Path outline',
  // The design draws streets as plain grey bands, without the white casing.
  'Minor road outline',
  'Major road outline',
  'Aqueduct outline',
  'Aqueduct',
  'Cablecar',
  'Cablecar dash',
  'Housenumber',
  'Gondola',
  'Ferry',
  'Oneway',
  'Highway junction',
  'Public',
  'Sport',
  'Education',
  'Tourism',
  'Culture',
  'Shopping',
  'Food',
  'Transport',
  'Park',
  'Healthcare',
  'Station',
  'Airport gate',
  'Airport',
]);

const lineLabel = (color) => ({
  'text-color': color,
  'text-halo-color': HALO,
  'text-halo-width': 1.2,
  'text-halo-blur': 0,
});

const PAINT = {
  Background: { 'background-color': LAND },
  Meadow: { 'fill-color': PARK, 'fill-opacity': 0.7 },
  Scrub: { 'fill-color': PARK, 'fill-opacity': 0.7 },
  Forest: { 'fill-color': PARK, 'fill-opacity': 0.8 },
  Wood: { 'fill-color': PARK, 'fill-opacity': 0.9 },
  Grass: { 'fill-color': PARK, 'fill-opacity': 1 },
  Pedestrian: { 'fill-color': '#F2F2EC', 'fill-outline-color': '#F2F2EC' },
  'River tunnel': { 'line-color': WATER },
  River: { 'line-color': WATER },
  'Water intermittent': { 'fill-color': WATER },
  Water: { 'fill-color': WATER },
  Aeroway: { 'line-color': ROAD_MINOR },
  'Tunnel outline': { 'line-color': ROAD, 'line-opacity': 0.5 },
  Tunnel: { 'line-color': ROAD_MINOR, 'line-opacity': 0.7 },
  'Railway tunnel': { 'line-color': RAIL, 'line-opacity': 0.6 },
  'Footway tunnel': { 'line-color': PATH },
  Pier: { 'fill-color': LAND },
  'Pier road': { 'line-color': PATH },
  'Bridge outline': { 'line-color': HIGHWAY_EDGE },
  Bridge: { 'fill-color': LAND },
  'Highway outline': { 'line-color': HIGHWAY_EDGE },
  'Road under construction': { 'line-color': ROAD_MINOR },
  'Minor road': { 'line-color': ROAD_MINOR },
  'Major road': { 'line-color': ROAD },
  Highway: { 'line-color': HIGHWAY },
  'Path minor': { 'line-color': PATH },
  Path: { 'line-color': PATH },
  'Major rail': { 'line-color': RAIL },
  'Minor rail': { 'line-color': RAIL },
  // No outlines: at city zoom they turned the blocks into noise.
  Building: { 'fill-color': BUILDING, 'fill-opacity': 1, 'fill-outline-color': BUILDING },
  'Building 3D': { 'fill-extrusion-color': BUILDING, 'fill-extrusion-opacity': 0.7 },
  'Other border': { 'line-color': BORDER },
  'Disputed border': { 'line-color': BORDER },
  'Country border': { 'line-color': BORDER },
  'River labels': lineLabel(WATER_LABEL),
  'Ocean labels': lineLabel(WATER_LABEL),
  'Lake labels': lineLabel(WATER_LABEL),
  'Road labels': lineLabel(LABEL),
  // Districts large and grey, towns and cities black, as in the mockups.
  'Place labels': lineLabel(LABEL),
  'State labels': lineLabel(LABEL),
  'Town labels': { ...lineLabel(LABEL_STRONG), 'icon-color': LABEL_STRONG },
  'City labels': { ...lineLabel(LABEL_STRONG), 'icon-color': LABEL_STRONG },
  'Capital city labels': { ...lineLabel(LABEL_STRONG), 'icon-color': LABEL_STRONG },
  'Country labels': lineLabel(LABEL),
  'Continent labels': lineLabel(LABEL),
};

export function applyProductTheme(layers) {
  for (const layer of layers) {
    if (HIDDEN.has(layer.id)) {
      layer.layout = { ...layer.layout, visibility: 'none' };
      continue;
    }
    const paint = PAINT[layer.id];
    if (paint) layer.paint = { ...layer.paint, ...paint };
  }
}
